import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RangeSelectHint, SelectAllCheckbox } from '@/components/ui/range-select';
import { applyToSet, useRangeSelection } from '@/hooks/useRangeSelection';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckboxField, Note, Segmented } from '../workspace/ui';
import { useDiscoveryClient } from '../api/context';
import { useCapabilities } from '../hooks/capabilities';
import { LEGACY_MAX_SEEDS, LookalikeSeedPicker, estimateDuration } from './LookalikeSeedPicker';
import { LookalikeSounds } from './LookalikeSounds';
import type {
  Folder,
  LookalikeAnalysis,
  LookalikeRunResult,
  TagCandidate,
} from '../api/types';

/**
 * Filters applied to the lookalike run. Seeded from the folder's most recent
 * search so "find more like these" inherits the thresholds that built the
 * folder — without them a lookalike run saves everything the hashtag returns,
 * which is not what you'd get from the same search by hand.
 */
interface RunFilters {
  minFollowers: string;
  maxFollowers: string;
  minAvgViews: string;
  maxAvgViews: string;
  maxLastPostedDays: string;
  emailFilter: string;
}

const EMPTY_FILTERS: RunFilters = {
  minFollowers: '',
  maxFollowers: '',
  minAvgViews: '',
  maxAvgViews: '',
  maxLastPostedDays: '',
  emailFilter: 'any',
};

const EMAIL_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'any-email', label: 'Has email' },
  { value: 'none', label: 'No email' },
  { value: 'agency', label: 'Agency only' },
  { value: 'generic', label: 'Generic only' },
];

/**
 * Lookalike search — deliberately a separate, manually-triggered flow.
 *
 * It fans out across hashtags harvested from the folder's own creators, which
 * is real RapidAPI spend, so it runs in two steps: analyse (show the tags,
 * costs a call per seed) then run (only after the tags are approved). Nothing
 * is searched on a guess.
 *
 * The tag picker exists because automated scoring can't be trusted blind —
 * `#edit` looks statistically strong (half the seeds use it) but returns edits
 * of anything. You're the one who knows which tags mean your niche.
 */

const BAND_LABEL: Record<TagCandidate['band'], string> = {
  niche: 'Niche',
  topic: 'Topic',
  broad: 'Broad',
};

function NumRange({
  label,
  min,
  max,
  onChange,
}: {
  label: string;
  min: string;
  max: string;
  onChange: (min: string, max: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          placeholder="Min"
          value={min}
          onChange={(e) => onChange(e.target.value, max)}
        />
        <span className="text-xs text-muted-foreground">–</span>
        <Input
          type="number"
          placeholder="Max"
          value={max}
          onChange={(e) => onChange(min, e.target.value)}
        />
      </div>
    </div>
  );
}

export function LookalikeDialog({
  folder,
  onClose,
  onStarted,
}: {
  folder: Folder;
  onClose: () => void;
  onStarted: (result: LookalikeRunResult) => void;
}): JSX.Element {
  const client = useDiscoveryClient();
  const [analysis, setAnalysis] = useState<LookalikeAnalysis | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createFolder, setCreateFolder] = useState(true);
  const [folderName, setFolderName] = useState(`${folder.name} — Lookalikes`);
  const [targetCount, setTargetCount] = useState(200);
  const [excludeOfficial, setExcludeOfficial] = useState(true);
  // A lookalike run is a search like any other, so it gets the same on-camera
  // filter — without it, "more like these" happily returns the clip farms the
  // original search was set up to exclude.
  const [presenterFilter, setPresenterFilter] = useState<'any' | 'faces' | 'faceless'>('any');
  const presenterCheckAvailable = useCapabilities().presenterCheck;
  const [filters, setFilters] = useState<RunFilters>(EMPTY_FILTERS);
  const [inheritedFrom, setInheritedFrom] = useState(false);
  const [seedCount, setSeedCount] = useState(15);

  // A 404 here is an older discovery build without the route — the picker
  // falls back to its old 40-seed ceiling rather than blocking the dialog.
  const seedsQuery = useQuery({
    queryKey: ['lookalike-seeds', folder.publicId],
    queryFn: () => client.folders.lookalikeSeeds(folder.publicId),
    retry: false,
  });
  useEffect(() => {
    const c = seedsQuery.data;
    if (c) setSeedCount(Math.max(2, Math.min(c.eligible, c.max, LEGACY_MAX_SEEDS)));
  }, [seedsQuery.data]);

  const analyzeMutation = useMutation({
    mutationFn: () => client.folders.analyzeLookalikes(folder.publicId, { maxSeeds: seedCount }),
    onSuccess: (data) => {
      setAnalysis(data);
      setSelected(new Set(data.suggested));
      if (data.lastFilters) {
        const f = data.lastFilters;
        const str = (v: number | null) => (v == null ? '' : String(v));
        setFilters({
          minFollowers: str(f.minFollowers),
          maxFollowers: str(f.maxFollowers),
          minAvgViews: str(f.minAvgViews),
          maxAvgViews: str(f.maxAvgViews),
          maxLastPostedDays: str(f.maxLastPostedDays),
          emailFilter: f.emailFilter || 'any',
        });
        setInheritedFrom(
          Object.values(f).some((v) => v != null && v !== 'any' && v !== ''),
        );
      }
    },
  });
  const runMutation = useMutation({
    mutationFn: () =>
      client.folders.runLookalike(folder.publicId, {
        hashtags: Array.from(selected),
        createFolder,
        folderName: createFolder ? folderName : undefined,
        targetCount,
        excludeOfficial,
        ...(presenterFilter !== 'any' && presenterCheckAvailable ? { presenterFilter } : {}),
        ...num('minFollowers'),
        ...num('maxFollowers'),
        ...num('minAvgViews'),
        ...num('maxAvgViews'),
        ...num('maxLastPostedDays'),
        ...(filters.emailFilter !== 'any' ? { emailFilter: filters.emailFilter } : {}),
      }),
    onSuccess: onStarted,
  });

  /** Only send a filter that's actually set — blank means "no constraint". */
  function num(key: keyof RunFilters): Record<string, number> {
    const raw = filters[key].trim();
    if (raw === '') return {};
    const n = Number(raw);
    return Number.isFinite(n) ? { [key]: n } : {};
  }

  function setFilter<K extends keyof RunFilters>(key: K, value: RunFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setInheritedFrom(false);
  }

  const busy = analyzeMutation.isPending;
  // Sounds go to the queue with the tags picked here (or the suggested ones) as topic words.
  const sharedSounds = analysis ? (
    <LookalikeSounds
      sounds={analysis.sounds ?? []}
      seedsUsed={analysis.seedsUsed.length}
      folderPublicId={folder.publicId}
      topicTerms={(selected.size > 0 ? Array.from(selected) : analysis.suggested).slice(0, 8)}
    />
  ) : null;
  const candidates = analysis?.candidates ?? [];
  const range = useRangeSelection({
    visible: candidates.map((c) => c.tag),
    isSelected: (tag) => selected.has(tag),
    onApply: applyToSet(setSelected),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-2xl sm:p-0">
        <DialogHeader className="border-b border-border px-5 py-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" strokeWidth={2} />
            Find lookalikes
          </DialogTitle>
          <DialogDescription>
            Hashtags shared by the creators already in <strong className="text-foreground">{folder.name}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!analysis && !busy && (
            <LookalikeSeedPicker
              counts={seedsQuery.data}
              loading={seedsQuery.isLoading}
              value={seedCount}
              onChange={setSeedCount}
            />
          )}

          {busy && (
            <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading recent posts from {seedCount.toLocaleString()} creators… this takes{' '}
              {estimateDuration(seedCount)}.
            </div>
          )}

          {analyzeMutation.error && <Note tone="error">{(analyzeMutation.error as Error).message}</Note>}

          {analysis && !busy && candidates.length === 0 && (
            <p className="py-8 text-sm text-muted-foreground">
              No shared hashtags found. This folder's creators don't post under common tags, so
              lookalike search can't infer a niche from them.{' '}
              <button type="button" className="underline" onClick={() => setAnalysis(null)}>
                Read a different number of creators
              </button>
            </p>
          )}
          {analysis && !busy && candidates.length === 0 && sharedSounds}

          {analysis && candidates.length > 0 && (
            <>
              <p className="mb-3 text-sm text-muted-foreground">
                Read {analysis.postsScanned} recent posts from {analysis.seedsUsed.length} creators
                and found {analysis.hashtagsSeen} hashtags. These are the ones used by at least{' '}
                {analysis.diagnostics?.minSeedCount ?? 2} of them, ranked by how specific they are.{' '}
                <button type="button" className="underline" onClick={() => setAnalysis(null)}>
                  Change
                </button>
              </p>

              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-3">
                <label className="inline-flex cursor-pointer items-center gap-3 text-sm">
                  <SelectAllCheckbox label="Select every hashtag" state={range.headState} onToggle={range.toggleAll} />
                  {selected.size} of {candidates.length} selected
                </label>
                <RangeSelectHint show={selected.size > 0} />
              </div>

              <ul className="space-y-1">
                {candidates.map((c) => {
                  const on = selected.has(c.tag);
                  return (
                    <li key={c.tag}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                          on ? 'border-brand/50 bg-brand/10' : 'border-border hover:bg-accent/50',
                        )}
                        onMouseDown={range.onMouseDown}
                      >
                        <input type="checkbox" className="h-4 w-4 accent-[var(--brand)]" {...range.checkboxProps(c.tag)} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          #{c.tag}
                        </span>
                        <span
                          className={cn(
                            'rounded-md border px-1.5 py-0.5 text-xs font-medium',
                            c.band === 'niche'
                              ? 'border-success/35 bg-success/10 text-success'
                              : c.band === 'topic'
                                ? 'border-border bg-muted/50 text-muted-foreground'
                                : 'border-warning/35 bg-warning/10 text-warning',
                          )}
                          title={
                            c.band === 'niche'
                              ? 'Specific — most results should be on-topic'
                              : c.band === 'topic'
                                ? 'Covers the subject, will include some fans'
                                : 'Very large — expect a lot of noise'
                          }
                        >
                          {BAND_LABEL[c.band]}
                        </span>
                        <span className="w-28 text-right text-xs tabular-nums text-muted-foreground">
                          {c.reach.toLocaleString()} creators
                        </span>
                        <span
                          className="w-16 text-right text-xs tabular-nums text-muted-foreground"
                          title={`${c.seedCount} of your creators use this tag`}
                        >
                          {c.seedCount}/{analysis.seedsUsed.length}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              {analysis.excluded.length > 0 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Excluded as filler:{' '}
                  {analysis.excluded.map((e) => `#${e.tag}`).join(', ')} — these describe format,
                  not subject, so they return content about anything.
                </p>
              )}

              {sharedSounds}

              <div className="mt-5 space-y-3 border-t border-border pt-4">
                <div>
                  <div className="mb-2 flex items-baseline gap-2">
                    <h4 className="text-sm font-semibold">Filters for this run</h4>
                    {inheritedFrom && (
                      <span className="text-sm text-muted-foreground">
                        carried over from this folder's last search
                      </span>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <NumRange
                      label="Followers"
                      min={filters.minFollowers}
                      max={filters.maxFollowers}
                      onChange={(mn, mx) => {
                        setFilter('minFollowers', mn);
                        setFilter('maxFollowers', mx);
                      }}
                    />
                    <NumRange
                      label="Avg views"
                      min={filters.minAvgViews}
                      max={filters.maxAvgViews}
                      onChange={(mn, mx) => {
                        setFilter('minAvgViews', mn);
                        setFilter('maxAvgViews', mx);
                      }}
                    />
                    <div className="grid gap-1">
                      <span className="text-sm font-medium">Email</span>
                      <Combobox
                        value={filters.emailFilter}
                        onChange={(v) => setFilter('emailFilter', v || 'any')}
                        options={EMAIL_OPTIONS}
                      />
                    </div>
                    <label className="grid gap-1">
                      <span className="text-sm font-medium">
                        Posted within (days)
                      </span>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Any"
                        value={filters.maxLastPostedDays}
                        onChange={(e) => setFilter('maxLastPostedDays', e.target.value)}
                      />
                    </label>
                  </div>
                </div>

                <CheckboxField checked={excludeOfficial} onChange={setExcludeOfficial}>
                  Skip official and news/gossip accounts
                </CheckboxField>
                <div className="grid gap-1">
                  <span className="text-sm font-medium">On camera</span>
                  <Segmented
                    label="On camera"
                    value={presenterCheckAvailable ? presenterFilter : 'any'}
                    onChange={(v) => presenterCheckAvailable && setPresenterFilter(v)}
                    options={[
                      { value: 'any', label: 'Anyone' },
                      { value: 'faces', label: 'Face creators' },
                      { value: 'faceless', label: 'Faceless (clips, edits)' },
                    ]}
                    className={cn('w-fit', !presenterCheckAvailable && 'pointer-events-none opacity-50')}
                  />
                  <span className="text-sm text-muted-foreground">
                    {presenterCheckAvailable
                      ? 'Checks recent thumbnails for a recurring on-camera presenter.'
                      : 'Not configured on this server.'}
                  </span>
                </div>
                <CheckboxField checked={createFolder} onChange={setCreateFolder}>
                  Put results in their own folder
                </CheckboxField>
                {createFolder && (
                  <Input
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className="max-w-sm"
                    maxLength={120}
                  />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Stop after</span>
                  <Input
                    type="number"
                    min={1}
                    max={2000}
                    value={targetCount}
                    onChange={(e) => setTargetCount(Number(e.target.value) || 100)}
                    className="w-24"
                  />
                  <span className="text-muted-foreground">creators</span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {!analysis
              ? 'Hashtags come next — nothing is searched until you pick them'
              : selected.size > 0
                ? `${selected.size} hashtag${selected.size === 1 ? '' : 's'} selected`
                : 'Pick at least one hashtag'}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {!analysis ? (
              <Button
                onClick={() => analyzeMutation.mutate()}
                disabled={busy || seedsQuery.isLoading || seedsQuery.data?.eligible === 0}
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Reading…
                  </>
                ) : (
                  `Read ${seedCount.toLocaleString()} creators`
                )}
              </Button>
            ) : (
              <Button
                onClick={() => runMutation.mutate()}
                disabled={selected.size === 0 || runMutation.isPending || busy}
              >
                {runMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Starting…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Find lookalikes
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        {runMutation.error && (
          <div className="px-5 pb-4">
            <Note tone="error">{(runMutation.error as Error).message}</Note>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
